import { useCallback, useMemo, useState } from 'react';
import {
    BooleanField,
    Button,
    ChipField,
    Confirm,
    CreateButton, ExportButton, FilterButton, Identifier, ImageField, ImageFieldProps, RaRecord, Resource, RowClickFunction, TextInput, TopToolbar,
    WrapperField,
    useRecordContext, useUpdate
} from 'react-admin';
import { MediaBrowse } from './media-forms';

export const defaultListFilters = [
	<TextInput source="q" label="Search" alwaysOn resettable />,
];

interface ListActionsProps {
    filter?: boolean;
    create?: boolean;
    exportCsv?: boolean;
}

export const ListActions = (props: ListActionsProps) => {
    const {
        filter = false,
        create = true,
        exportCsv = false,
    } = props;
    return (
        <TopToolbar>
            { filter ?
            <FilterButton/>
            : null }
            { create ?
            <CreateButton/>
            : null }
            { exportCsv ?
            <ExportButton/>
            : null }
        </TopToolbar>
    );
}

const thumbnailStyle: any = {
    maxWidth: 100,
    maxHeight: 100,
    objectFit: 'contain',
}

const previewStyle: any = {
    maxWidth: 600,
    maxHeight: 400,
    objectFit: 'contain',
}

export const MediaPreview = (props: { isThumbnail?: boolean } & ImageFieldProps) => {
    const { source = '', isThumbnail } = props;
    const record = useRecordContext();
    const style = isThumbnail === true ? thumbnailStyle : previewStyle ;

    const filename = (record[source] as string)?.toLowerCase() || '';
    const mimeType = record?.mimeType || '';

    if (
        mimeType.startsWith('image')
        || filename.endsWith('jpg')
        || filename.endsWith('png')
    ) {
        return <ImageField source={ source } sx={{ '& img': style }} title={ record.title } sortable={ false } />
    } else if (mimeType.startsWith('video')) {
        return (
            <WrapperField source={ source } sortable={ false }>
                <video controls style={ style } title={ record.title }>
                    <source src={ record[source] } type={ record.mimeType } />
                </video>
            </WrapperField>
        )
    } else {
        return <p>Preview not available</p>
    }
}

export const ColorChipField = (props: any) => {
    const record = useRecordContext();
    return (
        <ChipField
            label={ props.label }
            source={ props.source }
            sx={{
                color: record.color,
                backgroundColor: record.bgColor,
            }}
        />
    )
}

export const ColorBooleanField = (props: any) => {
    const record = useRecordContext();
    return (
        <BooleanField source={ props.source }
            sx={{
                color: record[props.source] ? 'green' : 'red',
            }}
        />
    )
}

interface UpdateProps {
    resource: string;
    source: string;
    buttonLabel?: string;
}

export const UpdateUrlFieldButton = (props: UpdateProps) => {
    const [open, setOpen] = useState(false);
    const record = useRecordContext();
    const [url, setUrl] = useState('');

    const diff = useMemo(() => {
        return {
            [props.source]: url,
        }
    }, [props.source, url]);

    const handleClick = () => {
        setSelectedIds([])
        setOpen(true)
    }
    const handleDialogClose = () => {
        setSelectedIds([])
        setOpen(false)
    }
    const handleConfirm = () => {
        update(props.resource, diff)
        setSelectedIds([])
        setOpen(false);
    };

    const [update, { isLoading }] = useUpdate(
        props.resource,
        { id: record.id, data: diff, previousData: record },
    );

    const title = props.buttonLabel || 'Update';

    const [selectedIds, setSelectedIds] = useState([] as Identifier[])

    const onRowClick: RowClickFunction = useCallback((id: Identifier, resource: string, selectedMedia: RaRecord) => {
        if (selectedIds.indexOf(id) >= 0) {
            setSelectedIds([])
        } else {
            setSelectedIds([id])
        }
        setUrl(selectedMedia.url)
        return 'toggleSelection'
    }, [selectedIds]);

    return (
        <>
            <Button label={ title } onClick={handleClick} />
            <Confirm
                isOpen={open}
                loading={isLoading}
                title={ title }
                content={
                    <Resource name='media'
                        list={
                            <MediaBrowse
                                rowClick={ onRowClick }
                                selectedIds={ selectedIds }
                            />
                        }
                    />
                }
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                className='MediaBrowseDialog'
            />
        </>
    );
};

export const EmptyBulkActionButtons = () => (
    <>
    </>
);

/**
 * Hide File Upload functionality
 *
 * https://github.com/nhn/tui.editor/issues/1204#issuecomment-1068364431
 */
export const hideFileUpload = function () {
    const observer = new MutationObserver(function () {
        if (document.querySelector('.toastui-editor-popup.toastui-editor-popup-add-image')) {
            const urlElement = document.querySelector('[aria-label="URL"]');
            // @ts-ignore
            urlElement && urlElement.click();
            const fileElement = document.querySelector('[aria-label="File"]');
            if (fileElement) {
                // @ts-ignore
                fileElement.style.display = "none";
            }
        }
    });

    const target = document.querySelector(".toastui-editor-popup ");
    if (target) {
        observer.observe(target, { attributes: true, attributeFilter: ["style"] });
    }
}